import { useMemo } from 'react';
import { differenceInDays, addDays, startOfDay } from 'date-fns';
import type { MonthMarker, QuarterMarker, DeliverableTimelineData } from '@/types/timeline';
import { TIMELINE_LAYOUT } from '@/constants/timeline';
import { isDueBeforeStart } from '@/lib/utils';

interface UseMonthMarkersOptions {
  /** Flattened timeline data for date range calculation */
  flatData: DeliverableTimelineData[];
  /** Width of the chart area in pixels */
  chartWidth: number;
  /** Simulation start date — due dates before it cannot stretch the axis */
  simulationStartDate: Date;
}

interface UseMonthMarkersResult {
  /** Start date of the timeline (derived from data) */
  minDate: Date;
  /** End date of the timeline (latest date + buffer) */
  maxDate: Date | null;
  /** Total days in the timeline */
  totalDays: number;
  /** Month markers for the timeline header (bottom row) */
  monthMarkers: MonthMarker[];
  /** Quarter markers for the timeline header (top row) */
  quarterMarkers: QuarterMarker[];
  /** Convert a date to X position in pixels */
  dateToX: (date: Date | null) => number;
}

/**
 * Hook to generate two-layer timeline markers
 *
 * Generates:
 * - Quarter markers (top row): "Q1 2026", "Q2 2026", etc.
 * - Month markers (bottom row): "1", "2", "3", etc.
 *
 * @example
 * ```tsx
 * const { monthMarkers, quarterMarkers, dateToX } = useMonthMarkers({
 *   flatData,
 *   chartWidth: 800,
 * });
 * ```
 */
export function useMonthMarkers({
  flatData,
  chartWidth,
  simulationStartDate,
}: UseMonthMarkersOptions): UseMonthMarkersResult {
  // Calculate global date range from actual data (no dependency on current date)
  const { minDate, maxDate, totalDays } = useMemo(() => {
    let min: Date | null = null;
    let max: Date | null = null;

    for (const data of flatData) {
      const dates = [data.startP20, data.finishP95].filter(Boolean) as Date[];
      // A due date before the simulation start is unmeetable (flagged by the
      // label-column warning) — including it would stretch the axis into dead
      // space and compress every forecast bar to the right (shipcast-hoi).
      if (data.dueDate && !isDueBeforeStart(data.dueDate, simulationStartDate)) {
        dates.push(data.dueDate);
      }

      for (const d of dates) {
        if (!min || d < min) min = d;
        if (!max || d > max) max = d;
      }
    }

    // Start flush with data, add small buffer after max date
    if (min) min = startOfDay(min);
    if (max) max = addDays(max, TIMELINE_LAYOUT.DATE_BUFFER_DAYS_END);

    const total = min && max ? differenceInDays(max, min) : 0;

    return { minDate: min ?? startOfDay(new Date()), maxDate: max, totalDays: total };
  }, [flatData, simulationStartDate]);

  // Convert date to X position (rounded to whole pixels for crisp SVG rendering)
  const dateToX = useMemo(() => {
    return (date: Date | null): number => {
      if (!date || !minDate || totalDays === 0) return 0;
      const days = differenceInDays(date, minDate);
      return Math.round((days / totalDays) * chartWidth);
    };
  }, [minDate, totalDays, chartWidth]);

  // Generate month markers (bottom row: individual months with numbers 1-12)
  const monthMarkers = useMemo<MonthMarker[]>(() => {
    if (!minDate || !maxDate) return [];

    const markers: MonthMarker[] = [];
    let current = new Date(minDate.getFullYear(), minDate.getMonth(), 1);

    while (current <= maxDate) {
      const x = dateToX(current);
      if (x >= 0 && x <= chartWidth) {
        markers.push({
          date: current,
          x,
          label: String(current.getMonth() + 1), // 1-12
        });
      }
      current = new Date(current.getFullYear(), current.getMonth() + 1, 1);
    }

    return markers;
  }, [minDate, maxDate, chartWidth, dateToX]);

  // Generate quarter markers (top row: Q1-Q4 spans with year)
  const quarterMarkers = useMemo<QuarterMarker[]>(() => {
    if (!minDate || !maxDate || monthMarkers.length === 0) return [];

    const markers: QuarterMarker[] = [];

    // Helper to get quarter number (1-4) from month (0-11)
    const getQuarter = (month: number): number => Math.floor(month / 3) + 1;

    // Group months by quarter
    let currentQuarter: { year: number; quarter: number; startX: number; months: MonthMarker[] } | null = null;

    for (const month of monthMarkers) {
      const year = month.date.getFullYear();
      const quarter = getQuarter(month.date.getMonth());

      if (!currentQuarter || currentQuarter.year !== year || currentQuarter.quarter !== quarter) {
        // Save previous quarter if exists
        if (currentQuarter && currentQuarter.months.length > 0) {
          const lastMonth = currentQuarter.months[currentQuarter.months.length - 1]!;
          // End X is the start of the next month (or chart width for last quarter)
          const nextMonthIndex = monthMarkers.indexOf(lastMonth) + 1;
          const endX = nextMonthIndex < monthMarkers.length ? monthMarkers[nextMonthIndex]!.x : chartWidth;

          markers.push({
            startX: currentQuarter.startX,
            endX,
            label: `Q${currentQuarter.quarter} ${currentQuarter.year}`,
            year: currentQuarter.year,
            quarter: currentQuarter.quarter,
          });
        }

        // Start new quarter
        currentQuarter = {
          year,
          quarter,
          startX: month.x,
          months: [month],
        };
      } else {
        currentQuarter.months.push(month);
      }
    }

    // Don't forget the last quarter
    if (currentQuarter && currentQuarter.months.length > 0) {
      markers.push({
        startX: currentQuarter.startX,
        endX: chartWidth,
        label: `Q${currentQuarter.quarter} ${currentQuarter.year}`,
        year: currentQuarter.year,
        quarter: currentQuarter.quarter,
      });
    }

    return markers;
  }, [minDate, maxDate, monthMarkers, chartWidth]);

  return { minDate, maxDate, totalDays, monthMarkers, quarterMarkers, dateToX };
}
